import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ERPSBaseDatePicker from "../custom/inputs/ERPSDatePicker";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import actions from "../../actions";
import { withSidebar } from "../hoc/withSidebar";
import warningIcon from "../../assets/images/warning-icon.png";
import ErrorMessage from "../custom/containers/ErrorMessage";
import {
  getBatchHolidayHeading,
  EMPTY_HOLIDAY_CRITERIA,
} from "../../constants/batchHolidayConstant";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ERPSBaseDialog from "../custom/inputs/style/ERPSBaseModelDialogStyle";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import { statisticsFormatDate } from "./../../utils/helper";
import moment from "moment";
import { ResponsiveBox, Box, DialogActions, DialogContent, Grid, Icon, Typography } from "../primitives";

const BatchHoliday = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    history,
    location: {
      pathname,
      state: { componentAccess, ...restProps },
    },
  } = props;
  const defaultValues = {
    holidayDate: null,
    holidayDescription: "",
    descriptionErrorHighlight: false,
    dateErrorHighlight: false,
  };

  const errorRef = useRef();
  const [error, setError] = useState([]);
  const [pageNumber, setPage] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(25);
  const [holidayDetails, setHolidayDetails] = useState(EMPTY_HOLIDAY_CRITERIA);
  const [values, setValues] = useState(defaultValues);
  const [loader, setLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState({});
  const [displayDialog, setDisplayDialog] = useState(false);
  const [holidayData, setHolidayData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);

  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const selectedFiNumber = useSelector((state) => state.fiReducer.selectedFi);
  const batchHolidayResult = useSelector(
    (state) => state.batchHolidayReducer.holidayList
  );
  const fiNumber = useSelector(
    (state) => state.loginUserReducer.user?.fiNumber
  );
  const successMessage = useSelector(
    (state) => state.batchHolidayReducer?.success
  );

  const successRef = useRef(null);

  useEffect(() => {
    if (successRef.current) {
      successRef.current.focus();
    }
  }, [successMessage && showSuccessMessage]);
  const fetchHoliday = () => {
    dispatch(actions.batchHolidayActions.getHoliday(0, 25, 0, t, 25));
  };

  const handleOpenDialog = () => {
    setDisplayDialog(true);
  };

  const handleCloseDialog = () => {
    setDisplayDialog(false);
    setDeleteRecord({});
  };

  useEffect(() => {
    fetchHoliday();
  }, []);

  useEffect(() => {
    if (batchHolidayResult) {
      setHolidayData(batchHolidayResult.data);
    }
    if (batchHolidayResult?.holidayDetails) {
      const { startOffset, endOffset } = batchHolidayResult?.holidayDetails;
      const rowPerPage = endOffset - startOffset;
      const pageNumber = endOffset / (endOffset - startOffset) - 1;
      setPage(pageNumber);
      setRowPerPage(rowPerPage);
      setHolidayDetails(batchHolidayResult?.holidayDetails);
    }
  }, [batchHolidayResult]);

  const handleFormChange = (e) => {
    const value = e.target.value;
    setValues({
      ...values,
      [e.target.id]: value,
    });
  };

  const handlePeriodChange = (value) => {
    setValues({
      ...values,
      holidayDate: value,
    });
  };

  const cancelHoliday = () => {
    setValues(defaultValues);
    setError([]);
    setLoader(false);
    setDeleteRecord({});
  };

  const validateBatchHoliday = () => {
    let errormsg = [];
    values.descriptionErrorHighlight = false;
    values.dateErrorHighlight = false;
    if (!values.holidayDate) {
      values.dateErrorHighlight = true;
      errormsg.push(t("erps.batchholiday.page.error.dateNotBlank"));
    }
    if (values.holidayDate == "Invalid date") {
      values.dateErrorHighlight = true;
      errormsg.push(t("erps.batchholiday.page.error.dateInvalid"));
    } else {
      const enteredDate = moment(values.holidayDate);
      const todayDate = moment();
      if (enteredDate.isBefore(todayDate, "day")) {
        values.dateErrorHighlight = true;
        errormsg.push(t("erps.batchholiday.page.error.dateInvalid"));
      }
    }
    if (!values.holidayDescription) {
      values.descriptionErrorHighlight = true;
      errormsg.push(t("erps.batchholiday.page.error.descriptionNotBlank"));
    }
    return errormsg;
  };

  const createHolidayBatch = () => {
    const errormsg = validateBatchHoliday();
    setError(errormsg);
    if (!errormsg.length) {
      setLoader(true);
      dispatch(
        actions.batchHolidayActions.createHoliday(
          {
            ...values,
            fiNumber: selectedFiNumber ? selectedFiNumber : fiNumber,
            holidayDateFormatStr: statisticsFormatDate(values?.holidayDate),
          },
          setValues,
          {
            ...props,
            setLoader,
            loader,
            setShowSuccessMessage,
            defaultValues,
            t,
          }
        )
      );
    }
  };

  const deleteHolidayAlert = (data) => {
    handleOpenDialog();
    setDeleteRecord({
      holidayDate: data?.row?.holidayDate,
      holidayDescription: data?.row?.holidayDescription,
      id: data?.row?.id,
    });
  };

  const deleteHoliday = () => {
    setDeleteLoader(true);
    dispatch(
      actions.batchHolidayActions.deleteHoliday(
        {
          ...deleteRecord,
          holidayDateFormatStr: statisticsFormatDate(deleteRecord?.holidayDate),
        },
        setValues,
        {
          ...props,
          setLoader,
          loader,
          setShowSuccessMessage,
          defaultValues,
          setDisplayDialog,
          setDeleteLoader,
          t,
        }
      )
    );
  };

  const sanitizeQueryParams = (jsonObj) => {
    let updatedReqBody;
    Object.keys(jsonObj).forEach((key) => {
      if (
        jsonObj[key] === undefined ||
        ("string" === typeof jsonObj[key] && jsonObj[key].trim() === "")
      ) {
        delete jsonObj[key];
      } else {
        if (typeof jsonObj[key] === "string") {
          updatedReqBody = { ...updatedReqBody, [key]: jsonObj[key].trim() };
        } else {
          updatedReqBody = { ...updatedReqBody, [key]: jsonObj[key] };
        }
      }
    });
    return updatedReqBody;
  };

  const paginateHoliday = (queryParams) => {
    const actualCriteria = sanitizeQueryParams({ ...queryParams });
    const holidayDataResultCount = holidayData.count;
    const pagSize = actualCriteria.endOffset - actualCriteria.startOffset;
    if (actualCriteria.startOffset < holidayDataResultCount) {
      setHolidayDetails((prevState) => ({
        ...prevState,
        startOffset: actualCriteria.startOffset,
        endOffset: actualCriteria.endOffset,
      }));
      setInitialLoading(true);
      dispatch(
        actions.batchHolidayActions.getHoliday(
          holidayDataResultCount,
          pagSize,
          actualCriteria.startOffset,
          t,
          actualCriteria.endOffset,
          setInitialLoading
        )
      );
    }
  };

  return (
    <div role="main">
      {successMessage && showSuccessMessage && (
        <ConfirmationMessage
          message={successMessage}
          setShowMessage={setShowSuccessMessage}
          inputRef={successRef}
        />
      )}
      <ResponsiveBox >
        <Box ml={3}>
        <ResponsiveBox className="manageUsers__page__title">
          <Typography variant="h1">
            {t("erps.batchholiday.page.title")}
          </Typography>
        </ResponsiveBox>
        {loginUser?.role?.role_type == "PA" ? (
          <>
            <ResponsiveBox className="manageUsers__wrap__box">
              <Typography variant="body1">
                {t("erps.batchholiday.page.title.description")}
              </Typography>
            </ResponsiveBox>
            <ErrorMessage 
              error={error.length > 0 ? error : null}
              errorRef={errorRef}
              mt={2}
            />
            <ResponsiveBox style={{ width: "650px" }}>
              <Grid container mt={100}>
                <Grid item sm={4} lg={4}>
                  <ERPSBaseDatePicker
                    label={t("erps.batchholiday.page.holidayDate")}
                    value={values?.holidayDate}
                    onChange={handlePeriodChange}
                    minDate={new Date()}
                    maxDate={new Date("9999-12-31")}
                    required="true"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <ERPSBaseTextField
                      id={"holidayDescription"}
                      type="text"
                      label={t("erps.batchholiday.page.description")}
                      className="holiday_description"
                      size="small"
                      onChange={handleFormChange}
                      value={values?.holidayDescription}
                      required="true"
                      inputProps={{ maxLength: 50 }}
                      fullWidth={true}
                    />
                </Grid>
              </Grid>
            </ResponsiveBox>
            <ResponsiveBox mt={-3} flex spacing={2}>
              <ERPSBaseButton
                variant="secondary"
                className="button_with_margin48"
                label={t("erps.common.button.cancel")}
                onClick={cancelHoliday}
              />
              <ERPSBaseButton
                variant="primary"
                className="button_with_margin48"
                label={t("erps.batchholiday.page.button.add")}
                onClick={createHolidayBatch}
                loading={loader}
              />
            </ResponsiveBox>
            <ResponsiveBox mt={6}>
              <Typography variant="h2">
                {t("erps.batchholiday.page.holidayDates")}
              </Typography>
            </ResponsiveBox>
          </>
        ) : (
          ""
        )}
        <ResponsiveBox marginTop={1} marginBottom={1.5} flex>
          <Typography variant="body1">
            {holidayData ? holidayData?.count : 0}{" "}
            {t("erps.batchholiday.page.records")}
          </Typography>
        </ResponsiveBox>
        {holidayData?.count > 0 && (
          <ResponsiveBox className="holiday_table">
            <ResponsiveDataGrid
              data={{
                rows: holidayData ? holidayData?.data : [],
                count: holidayData && holidayData.count,
                columns: getBatchHolidayHeading(
                  loginUser?.role?.role_type,
                  deleteHolidayAlert
                ),
              }}
              onAnyPaginationAction={paginateHoliday}
              history={history}
              dataGridWidth={
                loginUser?.role?.role_type == "PA"
                  ? "dataGrid__box__container__batchHolidayPA"
                  : "dataGrid__box__container__batchHoliday"
              }
              componentAccess={componentAccess}
              pageNumber={pageNumber}
              rowPerPage={rowPerPage}
              initialLoading={initialLoading}
              tableLabelText={t("erps.batchholiday.page.title")}
            />
          </ResponsiveBox>
        )}
        <ERPSBaseDialog
          role="dialog"
          onClose={handleCloseDialog}
          aria-labelledby="batchholiday-dialog"
          open={displayDialog}
        >
          <DialogHeader
            id="batchholiday-dialog-title"
            onClose={handleCloseDialog}
            style={{
              fontFamily: "Roboto,Regular",
              fontSize: "18px",
              color: "#2a2c2e",
            }}
          >
            {t("erps.batchholiday.page.dialog.title")}
          </DialogHeader>
          <DialogContent>
            <Typography aria-describedby="dialog-message">
              {t("erps.batchholiday.page.dialog.description1")}
            </Typography>
            <Typography aria-describedby="dialog-message">
              {statisticsFormatDate(deleteRecord?.holidayDate)}{" "}
              {deleteRecord?.holidayDescription}
            </Typography>
            <Typography
              mt={3}
              gutterBottom
              aria-describedby="confirmation-message"
            >
              <strong>
                {t("erps.batchholiday.page.dialog.confirmationMessage")}
              </strong>
            </Typography>
          </DialogContent>
          <DialogActions className={"rightAlignDialogActions"}>
            <ERPSBaseButton
              variant="secondary"
              className="button_with_zeromargin"
              onClick={handleCloseDialog}
              label={t("erps.common.button.cancel")}
            />
            <ERPSBaseButton
              variant="primary"
              className="button_with_zeromargin"
              onClick={deleteHoliday}
              label={t("erps.batchholiday.page.button.delete")}
              loading={deleteLoader}
            />
          </DialogActions>
        </ERPSBaseDialog>
        </Box>
      </ResponsiveBox>
    </div>
  );
};

export default withSidebar(BatchHoliday);
